import Alpine from 'alpinejs';
import { gameStore } from './app.store.js';
import { apiUrl } from './utils/api.js';
import { hud } from './ui/hud.js';
import { tabs } from './ui/tabs.js';
import { toast } from './ui/toast.js';
import { consent } from './ui/consent.js';
import { stats } from './ui/stats.js';
import { maintenance } from './components/maintenance.js';
import { missions } from './features/missions.js';
import { travel } from './features/travel.js';
import { inventory } from './features/inventory.js';
import { economy } from './features/economy.js';
import { combat } from './features/combat.js';
import { shop } from './features/shop.js';
import { auth } from './features/auth.js';
import { daily } from './features/daily.js';
import { crafting } from './features/crafting.js';
import { adminPanel } from './features/admin.js';
import { debugStatus } from './debug/status.js';
import { initEconomyCharts } from './admin/economy.charts.js';
import { initPlayerEconomyChart } from './features/economy.mini-chart.js';
import { startGameLoop } from './utils/time.js';
import { SimpleReadCache } from './utils/cache.js';
import { settings } from './utils/settings.js';
import { i18n, t } from './i18n.js';
import { settingsPanel } from './features/settings.js';
import { triggerImmediateWarmup } from './utils/warmup.js';

// Expose debug flag to global context for Alpine.js templates
window.__VITE_DEBUG__ = import.meta.env.VITE_DEBUG_API;

// Initialize global cache and telemetry
const initializeCacheAndTelemetry = async () => {
  try {
    // Initialize cache
    window.SimpleReadCache = new SimpleReadCache();
    await window.SimpleReadCache.init();
    
    // Initialize telemetry for development
    if (import.meta.env.DEV) {
      window.__telemetry = {
        events: [],
        log: (event, data) => {
          const telemetryEvent = {
            timestamp: Date.now(),
            event,
            data: JSON.parse(JSON.stringify(data))
          };
          window.__telemetry.events.push(telemetryEvent);
          console.log('[Telemetry]', event, data);
          
          // Keep only last 100 events
          if (window.__telemetry.events.length > 100) {
            window.__telemetry.events = window.__telemetry.events.slice(-100);
          }
        },
        clear: () => {
          window.__telemetry.events = [];
        }
      };
      
      console.log('Development telemetry initialized');
    }
    
    // Initialize settings and i18n
    settings.init();
    i18n.init(settings.getLanguage());
    
    console.log('Cache, telemetry, settings and i18n initialized');
  } catch (error) {
    console.warn('Failed to initialize cache/telemetry/settings:', error);
  }
};

// Initialize immediately
initializeCacheAndTelemetry();

// Cloudflare Insights DISABLED - no RUM tracking to avoid CORS issues

// Wake up backend to avoid cold start delays
const wakeUpBackend = async () => {
  try {
    const { fetchHealth } = await import('./utils/health.js');
    await fetchHealth();
    console.log('Backend wake-up successful');
  } catch (error) {
    console.warn('Backend wake-up failed (expected for cold start):', error.message);
  }
};

// Wake up backend immediately
wakeUpBackend();

// Register service worker with enhanced offline support
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js?v=3', { scope: '/' });
      console.log('SW registered: ', registration);
      
      // Listen for SW updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available
              console.log('New app version available!');
              // Could show user notification here
            }
          });
        }
      });
      
      // Handle offline/online status
      window.addEventListener('online', () => {
        console.log('App is online');
        if (window.__telemetry) {
          window.__telemetry.log('network_status', { status: 'online' });
        }
      });
      
      window.addEventListener('offline', () => {
        console.log('App is offline');
        if (window.__telemetry) {
          window.__telemetry.log('network_status', { status: 'offline' });
        }
      });
      
    } catch (registrationError) {
      console.log('SW registration failed: ', registrationError);
      if (window.__telemetry) {
        window.__telemetry.log('sw_registration_failed', { error: registrationError.message });
      }
    }
  });
}

// Auto-logout listener
window.addEventListener('app:logout', () => {
  if (window.Alpine?.store('app')?.logout) {
    window.Alpine.store('app').logout();
  }
});

// Import startup manager
import { startupManager } from './utils/startup.js';

// Initialize Alpine store
Alpine.store('app', gameStore);

// Register Alpine components
Alpine.data('gameApp', () => ({
  async init() {
    console.log('Destiny Reborn v2 initialized');
    
    // Phase 1: Public initialization (no auth required)
    console.log('📡 Starting public initialization...');
    const publicConfig = await startupManager.initPublic();
    
    // Check maintenance mode
    if (startupManager.isMaintenanceMode()) {
      console.warn('⚠️ Maintenance mode active:', startupManager.getMaintenanceMessage());
      // Show maintenance UI if needed
    }
    
    // Phase 2: Authentication
    const store = this.$store.app;
    await store.initializeAuth();
    
    // Update startup manager with auth status
    startupManager.setAuthenticated(store.auth.isAuthenticated);
    
    // Phase 3: Protected initialization (only if authenticated)
    if (store.auth.isAuthenticated) {
      console.log('🔒 User authenticated, loading protected data...');
      await startupManager.initProtected(store);
      
      // Start game systems
      startGameLoop();
      store.startPeriodicSync();
    } else {
      console.log('🚪 Waiting for user authentication...');
    }
    
    // Initialize analytics (when enabled)
    this.initAnalytics();
    
    // Signal that app is ready
    window.__APP_READY__ = true;
    console.log('🎉 App initialization complete');
  },
  
  async initAnalytics() {
    // Initialize Fingerprint (for A/B testing) - always load for buckets
    try {
      const { initFingerprint } = await import('./analytics/fingerprint.js');
      await initFingerprint();
    } catch (error) {
      console.warn('Fingerprint initialization failed:', error);
    }
    
    // Analytics will be loaded by consent system when user accepts
  },
  
  // Dynamic imports for heavy modules
  async loadChart() {
    const { initEconomyChart } = await import('./features/economy-chart.js');
    return initEconomyChart();
  },
  
  async loadAnimations() {
    const { initAnimations } = await import('./utils/animations.js');
    return initAnimations();
  }
}));

// Register UI components
Alpine.data('hud', hud);
Alpine.data('tabs', tabs);
Alpine.data('toast', toast);
Alpine.data('consent', consent);
Alpine.data('stats', stats);
Alpine.data('maintenance', maintenance);

// Register feature components
Alpine.data('missions', missions);
Alpine.data('travel', travel);
Alpine.data('inventory', inventory);
Alpine.data('economy', economy);
Alpine.data('combat', combat);
Alpine.data('shop', shop);
Alpine.data('auth', auth);
Alpine.data('daily', daily);
Alpine.data('crafting', crafting);
Alpine.data('adminPanel', adminPanel);
Alpine.data('debugStatus', debugStatus);
Alpine.data('economyCharts', initEconomyCharts);
Alpine.data('playerEconomyChart', initPlayerEconomyChart);
Alpine.data('settingsPanel', settingsPanel);

// Start Alpine
Alpine.start();

console.log('Alpine.js initialized with stores and components');

// Trigger backend warmup after Alpine initialization
console.log('🔥 Triggering backend warmup...');
triggerImmediateWarmup();